import React, { useState } from "react";
import { motion } from "framer-motion";
import { 
  Building2, Briefcase, Users, UserCheck, Calendar, ArrowRight, 
  CheckCircle2, Clock, Award, Layers, Target, ChevronDown, ChevronUp
} from "lucide-react";

export const RoleFillProgress = ({ role }) => {
  if (!role) return null;
  const f = role.funnel || {};
  const approved = role.approvedHeadcount || 3;
  const joined = f.joined || 0;
  const vacanciesRemaining = Math.max(0, approved - joined);
  const fillProgressPct = Math.min(100, Math.round((joined / approved) * 100));

  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md mb-6" data-testid="position-fill-progress">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="text-sm font-medium text-white flex items-center gap-2">
          <Target className="w-4 h-4 text-cyan-400" /> Position Fill Progress
        </h4>
        <span className="text-xs font-mono font-bold text-cyan-300">
          {fillProgressPct}% Complete
        </span>
      </div>

      {/* Progress Bar */}
      <div className="w-full h-3 rounded-full bg-slate-800 overflow-hidden mb-4 border border-white/10">
        <div 
          className="h-full bg-gradient-to-r from-cyan-400 to-emerald-400 transition-all duration-500 rounded-full"
          style={{ width: `${Math.max(5, fillProgressPct)}%` }}
        />
      </div>

      {/* Headcount Breakdown Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-mono">
        <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Approved Headcount</span>
          <span className="text-white font-bold text-sm">{approved}</span>
        </div>
        <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Joined / Filled</span>
          <span className="text-emerald-300 font-bold text-sm">{joined}</span>
        </div>
        <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Vacancies Remaining</span>
          <span className="text-amber-300 font-bold text-sm">{vacanciesRemaining}</span>
        </div>
        <div className="p-2.5 rounded-xl bg-white/5 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Total Applications</span>
          <span className="text-cyan-300 font-bold text-sm">{f.received || 0}</span>
        </div>
      </div>
    </div>
  );
};

export const RoleHiringFunnel = ({ role }) => {
  if (!role) return null;
  const f = role.funnel || {};

  const stages = [
    { label: "Applications", count: f.received || 0, color: "text-blue-300 border-blue-500/30 bg-blue-500/10" },
    { label: "Under Review", count: f.under_review || 0, color: "text-purple-300 border-purple-500/30 bg-purple-500/10" },
    { label: "Shortlisted", count: f.shortlisted || 0, color: "text-cyan-300 border-cyan-500/30 bg-cyan-500/10" },
    { label: "Interview Scheduled", count: f.interview_scheduled || 0, color: "text-amber-300 border-amber-500/30 bg-amber-500/10" },
    { label: "Interview Completed", count: f.interview_completed || 0, color: "text-teal-300 border-teal-500/30 bg-teal-500/10" },
    { label: "Selected", count: f.selected || 0, color: "text-indigo-300 border-indigo-500/30 bg-indigo-500/10" },
    { label: "Offer Released", count: f.offer_released || 0, color: "text-pink-300 border-pink-500/30 bg-pink-500/10" },
    { label: "Offer Accepted", count: f.offer_accepted || 0, color: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
    { label: "Joined", count: f.joined || 0, color: "text-green-300 border-green-500/30 bg-green-500/20" },
  ];

  return (
    <div className="p-5 rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md mb-6" data-testid="role-hiring-funnel">
      <h4 className="text-sm font-medium text-white mb-4 flex items-center gap-2">
        <Layers className="w-4 h-4 text-cyan-400" /> Role Hiring Funnel
      </h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-9 gap-2">
        {stages.map((s, idx) => (
          <div key={idx} className={`p-3 rounded-xl border text-center font-mono ${s.color}`}>
            <div className="text-lg font-bold">{s.count}</div>
            <div className="text-[9px] uppercase tracking-wider font-sans opacity-80 mt-1 line-clamp-1">{s.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const DepartmentExecutiveSummary = ({ deptSummary }) => {
  if (!deptSummary) return null;

  return (
    <div className="p-6 rounded-2xl border border-cyan-500/30 bg-slate-900/60 backdrop-blur-md mb-8" data-testid="department-executive-summary">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <span className="text-xs text-cyan-300 font-mono uppercase tracking-wider">Department Executive Summary</span>
          <h3 className="text-xl font-light text-white">{deptSummary.department}</h3>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-mono font-medium border ${deptSummary.healthBadge}`}>
          Health Score: {deptSummary.healthScore}/100 ({deptSummary.healthStatus})
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 text-xs font-mono">
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Total Roles</span>
          <span className="text-white font-bold text-sm">{deptSummary.totalRoles}</span>
        </div>
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Active Hiring Roles</span>
          <span className="text-cyan-300 font-bold text-sm">{deptSummary.activeRoles}</span>
        </div>
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Total Applications</span>
          <span className="text-white font-bold text-sm">{deptSummary.totalApps}</span>
        </div>
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Avg Apps / Role</span>
          <span className="text-purple-300 font-bold text-sm">{deptSummary.avgAppsPerRole}</span>
        </div>
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Average CQI</span>
          <span className="text-emerald-300 font-bold text-sm">{deptSummary.avgCqi}</span>
        </div>
        <div className="p-3 rounded-xl bg-black/40 border border-white/5">
          <span className="text-white/40 block text-[10px] uppercase">Immediate Joiners</span>
          <span className="text-amber-300 font-bold text-sm">{deptSummary.immediateJoiners}</span>
        </div>
      </div>
    </div>
  );
};

export const RoleExecutiveSummary = ({ roleMetric }) => {
  if (!roleMetric) return null;

  return (
    <div className="p-6 rounded-2xl border border-white/10 bg-slate-900/40 backdrop-blur-md mb-8" data-testid="role-executive-summary">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <span className="text-xs text-cyan-300 font-mono uppercase tracking-wider">{roleMetric.department}</span>
          <h3 className="text-xl font-light text-white">{roleMetric.title}</h3>
        </div>
        <span className="px-3 py-1 rounded-full bg-cyan-400/10 text-cyan-300 border border-cyan-400/30 text-xs font-mono">
          Status: {roleMetric.status}
        </span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs font-mono p-4 rounded-xl bg-black/40 border border-white/5 mb-6">
        <div>
          <span className="text-white/40 block text-[10px] uppercase">Hiring Manager</span>
          <span className="text-white font-medium">{roleMetric.hiringManager}</span>
        </div>
        <div>
          <span className="text-white/40 block text-[10px] uppercase">Approved Openings</span>
          <span className="text-cyan-300 font-medium">{roleMetric.approvedHeadcount} Positions</span>
        </div>
        <div>
          <span className="text-white/40 block text-[10px] uppercase">Recruitment Stage</span>
          <span className="text-emerald-300 font-medium font-sans">Active Pipeline</span>
        </div>
        <div>
          <span className="text-white/40 block text-[10px] uppercase">Recruitment Health</span>
          <span className="text-purple-300 font-medium">92.0 / 100</span>
        </div>
      </div>

      <RoleFillProgress role={roleMetric} />
      <RoleHiringFunnel role={roleMetric} />
    </div>
  );
};
